import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { schoolYearService } from '../../services/schoolYearService';

const schema = yup.object({
  name: yup.string().required('Nombre del curso es requerido'),
  start_month: yup.number()
    .required('Mes de inicio es requerido')
    .min(1, 'Mes inválido')
    .max(12, 'Mes inválido'),
  start_year: yup.number()
    .required('Año de inicio es requerido')
    .min(2020, 'Año mínimo 2020')
    .max(2030, 'Año máximo 2030'),
  end_month: yup.number()
    .required('Mes de fin es requerido')
    .min(1, 'Mes inválido')
    .max(12, 'Mes inválido'),
  end_year: yup.number()
    .required('Año de fin es requerido')
    .min(2020, 'Año mínimo 2020')
    .max(2030, 'Año máximo 2030')
    .test('is-after-start', 'El año de fin debe ser mayor o igual al año de inicio', 
      function(value) {
        const { start_year } = this.parent;
        return value >= start_year;
      }),
  is_active: yup.boolean().default(false),
  description: yup.string().nullable()
});

const months = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

const SchoolYearForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      start_month: 9,
      start_year: new Date().getFullYear(),
      end_month: 8,
      end_year: new Date().getFullYear() + 1,
      is_active: false,
      description: ''
    }
  });

  const watchStartMonth = watch('start_month');
  const watchStartYear = watch('start_year');
  const watchEndMonth = watch('end_month');
  const watchEndYear = watch('end_year');

  useEffect(() => {
    if (isEdit) {
      loadSchoolYear();
    }
  }, [id]);

  const loadSchoolYear = async () => {
    try {
      setLoading(true);
      const response = await schoolYearService.getById(parseInt(id!));
      reset(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar curso');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNumber: number) => {
    const month = months.find(m => m.value === monthNumber);
    return month ? month.label : '';
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit) {
        await schoolYearService.update(parseInt(id!), data);
      } else {
        await schoolYearService.create(data);
      }

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/school-years');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar curso');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/school-years" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Curso Escolar' : 'Nuevo Curso Escolar'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Curso escolar guardado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información del Curso
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre del Curso *"
                          placeholder="Ej: Curso 2024-2025"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SchoolIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Fecha de Inicio
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Controller
                          name="start_month"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label="Mes"
                              error={!!errors.start_month}
                              helperText={errors.start_month?.message}
                              disabled={loading}
                            >
                              {months.map((month) => (
                                <MenuItem key={month.value} value={month.value}>
                                  {month.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Controller
                          name="start_year"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              type="number"
                              label="Año"
                              error={!!errors.start_year}
                              helperText={errors.start_year?.message}
                              disabled={loading}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Fecha de Fin
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Controller
                          name="end_month"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label="Mes"
                              error={!!errors.end_month}
                              helperText={errors.end_month?.message}
                              disabled={loading}
                            >
                              {months.map((month) => (
                                <MenuItem key={month.value} value={month.value}>
                                  {month.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Controller
                          name="end_year"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              type="number"
                              label="Año"
                              error={!!errors.end_year}
                              helperText={errors.end_year?.message}
                              disabled={loading}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {(watchStartMonth && watchStartYear && watchEndMonth && watchEndYear) && (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="info" icon={<CalendarIcon />}>
                        <Typography variant="body2">
                          Período seleccionado: <strong>
                            {getMonthName(watchStartMonth)} {watchStartYear} - {getMonthName(watchEndMonth)} {watchEndYear}
                          </strong>
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              disabled={loading}
                              color="success"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">
                                {field.value ? 'Curso Activo' : 'Curso Inactivo'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {field.value 
                                  ? 'Este será el curso utilizado por defecto en reportes y consultas'
                                  : 'Los reportes usarán otro curso activo o deberá seleccionarse manualmente'
                                }
                              </Typography>
                            </Box>
                          }
                          labelPlacement="end"
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          label="Descripción"
                          placeholder="Información adicional sobre el curso..."
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <InfoIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate('/school-years')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || success}
              >
                {loading ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default SchoolYearForm;